import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SolowayFinance.css";
import { getApiBaseUrl } from "../utils/apiConfig";

const API = getApiBaseUrl();
const getAdminHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('jwt_token') || localStorage.getItem('token')}`
});

/* -------- tiny count-up for card numbers -------- */
function CountUp({ to = 0, duration = 900, prefix = "", suffix = "", money = false }) {
  const [val, setVal] = useState(0);
  const start = useRef(null);
  const target = Number(to || 0);

  useEffect(() => {
    start.current = null;
    let raf = 0;

    const step = (ts) => {
      if (!start.current) start.current = ts;
      const p = Math.min(1, (ts - start.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return (
    <>
      {prefix}
      {money
        ? val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : Math.round(val).toLocaleString()}
      {suffix}
    </>
  );
}

/* -------- helpers -------- */
const moneyFmt = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    : "—";

/* -------- small helper to build query string -------- */
function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      sp.set(k, v);
    }
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export default function SolowayFinance() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [summary, setSummary] = useState({
    totalProfit: 0,
    hotelOwnerProfit: 0,
    guideProfit: 0,
  });

  const [rows, setRows] = useState([]);

  // filters (auto-applied)
  const [role, setRole] = useState(""); // '', 'Traveler', 'Guide', 'Hotel Owner'
  const [dateFrom, setDateFrom] = useState(""); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState(""); // YYYY-MM-DD
  const [fetching, setFetching] = useState(false);

  const cards = useMemo(
    () => [
      { title: "Total Profit", value: summary.totalProfit, sub: "All gained profits", icon: "💰", prefix: "LKR ", money: true },
      { title: "Hotel Owner Profit", value: summary.hotelOwnerProfit, sub: "Profit from Hotel Owners", icon: "🏨", prefix: "LKR ", money: true },
      { title: "Guide Profit", value: summary.guideProfit, sub: "Profit from Guides", icon: "🧭", prefix: "LKR ", money: true },
    ],
    [summary]
  );

  // initial load (no filters)
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setErr("");

        const [sumRes, listRes] = await Promise.all([
          fetch(`${API}/admin/solowayfinance/summary`, { headers: getAdminHeaders() }),
          fetch(`${API}/admin/solowayfinance`, { headers: getAdminHeaders() }),
        ]);
        const sum = await sumRes.json();
        const list = await listRes.json();

        if (!sumRes.ok) throw new Error(sum?.message || "Failed to fetch summary.");
        if (!listRes.ok) throw new Error(list?.message || "Failed to fetch list.");

        if (!mounted) return;
        setSummary({
          totalProfit: Number(sum.totalProfit) || 0,
          hotelOwnerProfit: Number(sum.hotelOwnerProfit) || 0,
          guideProfit: Number(sum.guideProfit) || 0,
        });
        setRows(Array.isArray(list) ? list : []);
      } catch (e) {
        if (mounted) setErr(e.message || "Load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  // auto-apply filters whenever role/dateFrom/dateTo change
  useEffect(() => {
    if (loading) return;

    const controller = new AbortController();
    const run = async () => {
      try {
        setFetching(true);
        setErr("");

        const params = { role, date_from: dateFrom, date_to: dateTo };
        const query = qs(params);

        const [sumRes, listRes] = await Promise.all([
          fetch(`${API}/admin/solowayfinance/summary${query}`, { headers: getAdminHeaders(), signal: controller.signal }),
          fetch(`${API}/admin/solowayfinance${query}`, { headers: getAdminHeaders(), signal: controller.signal }),
        ]);
        const sum = await sumRes.json();
        const list = await listRes.json();

        if (!sumRes.ok) throw new Error(sum?.message || "Failed to fetch summary.");
        if (!listRes.ok) throw new Error(list?.message || "Failed to fetch list.");

        setSummary({
          totalProfit: Number(sum.totalProfit) || 0,
          hotelOwnerProfit: Number(sum.hotelOwnerProfit) || 0,
          guideProfit: Number(sum.guideProfit) || 0,
        });
        setRows(Array.isArray(list) ? list : []);
      } catch (e) {
        if (e.name !== "AbortError") setErr(e.message || "Apply filters failed");
      } finally {
        setFetching(false);
      }
    };

    const t = setTimeout(run, 150);
    return () => {
      controller.abort();
      clearTimeout(t);
    };
  }, [role, dateFrom, dateTo, loading]);

  if (loading) {
    return (
      <div className="wd-page">
        <div className="wd-head">
          <div>
            <h2 className="wd-title">Soloway Finance</h2>
            <p className="muted">Loading…</p>
          </div>
        </div>
        <section className="wd-cards">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card stat" style={{ opacity: 0.6 }}>
              <div className="stat-top">
                <div className="stat-title">Loading…</div>
                <div className="lock" aria-hidden>…</div>
              </div>
              <div className="stat-value">—</div>
              <div className="stat-sub">—</div>
            </div>
          ))}
        </section>
        <section className="card table-card" style={{ padding: 24, color: "gray" }}>
          Loading profits…
        </section>
      </div>
    );
  }

  if (err) {
    return (
      <div className="wd-page">
        <div className="wd-head">
          <div>
            <h2 className="wd-title">Soloway Finance</h2>
            <p className="muted" style={{ color: "crimson" }}>{err}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wd-page">
      {/* header */}
      <div className="wd-head">
        <div>
          <h2 className="wd-title">Soloway Finance</h2>
          <p className="muted">Manage Soloway profit records</p>
        </div>
      </div>

      {/* stat cards (profits) */}
      <section className="wd-cards">
        {cards.map((s, i) => (
          <div key={i} className="card stat">
            <div className="stat-top">
              <div className="stat-title">{s.title}</div>
              <div className="lock" aria-hidden>{s.icon}</div>
            </div>
            <div className="stat-value">
              <CountUp to={s.value} prefix={s.prefix || ""} money={s.money || false} />
            </div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </section>

      {/* toolbar with auto-applied filters */}
      <div className="wd-toolbar" style={{ gap: 8 }}>
        <h3 className="table-title">Profit records</h3>

        <div className="filters" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="input"
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #e5e7eb", color: role ? "inherit" : "var(--muted)" }}
            title="Filter by role"
          >
            <option value="">All Roles</option>
            <option value="Traveler">Traveler</option>
            <option value="Guide">Guide</option>
            <option value="Hotel Owner">Hotel Owner</option>
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="input"
            title="From (created date)"
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #e5e7eb", color: dateFrom ? "inherit" : "var(--muted)" }}
          />
          <span className="muted">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="input"
            title="To (created date)"
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #e5e7eb", color: dateTo ? "inherit" : "var(--muted)" }}
          />

          {fetching ? <span className="muted" style={{ fontSize: 12 }}>Updating…</span> : null}
        </div>
      </div>

      {/* table */}
      <section className="card table-card">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 34 }}><input type="checkbox" aria-label="select all" /></th>
              <th>Finance ID</th>
              <th>User Name</th>
              <th>User Role</th>
              <th>Profit</th>
              <th>Date of Profit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td><input type="checkbox" aria-label={`select ${r.id}`} /></td>
                <td><span className="id-badge">{r.financeId}</span></td>
                <td>{r.userName}</td>
                <td className="muted">{r.role}</td>
                <td className="amount">LKR {moneyFmt(r.profit)}</td>
                <td className="muted nowrap">{fmtDate(r.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
